import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Breadcrumbs } from "../AdminLayout";
import { graphExplorerApi, graphApi } from "../content/api";
import { formatErr } from "../api";
import { toast, Toaster } from "sonner";
import { Network, ExternalLink } from "lucide-react";

const COLL_TO_TYPE_ROUTE = {
  articles: "articles", conditions: "conditions", faqs: "faqs", guidelines: "guidelines",
  research_papers: "research-papers", research: "research-updates",
  evidence_summaries: "evidence-summaries",
  treatments: "treatments", symptoms: "symptoms", investigations: "investigations",
  medicines: "medicines", trimesters: "trimesters",
  videos: "videos", patient_guides: "patient-guides", infographics: "infographics",
  books: "books", testimonials: "testimonials", media_appearances: "media-appearances",
  teaching_slides: "teaching-slides", clinical_tools: "clinical-tools",
  ai_prompts: "ai-prompts", ai_models: "ai-models",
};

const RADIUS_INNER = 130, RADIUS_OUTER = 240;

export default function GraphExplorer() {
  const [types, setTypes] = useState([]);
  const [entityType, setEntityType] = useState("conditions");
  const [q, setQ] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [hops, setHops] = useState(1);
  const [graph, setGraph] = useState(null);

  useEffect(() => {
    graphApi.taxonomy().then(t => setTypes(t.entity_types || [])).catch(() => {});
  }, []);
  useEffect(() => {
    const t = setTimeout(() => {
      if (!entityType) return;
      graphApi.searchEntities(entityType, q || "").then(setCandidates).catch(() => setCandidates([]));
    }, 200);
    return () => clearTimeout(t);
  }, [entityType, q]);

  const load = async (id) => {
    setSelected(id);
    try { setGraph(await graphExplorerApi.neighborhood(entityType, id, hops)); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  useEffect(() => { if (selected) load(selected); /* eslint-disable-next-line */ }, [hops]);

  const layout = useMemo(() => {
    if (!graph) return null;
    const cx = 380, cy = 260;
    const positions = {};
    const byDepth = { 0: [], 1: [], 2: [] };
    graph.nodes.forEach(n => byDepth[n.depth ?? 1].push(n));
    positions[graph.root] = { x: cx, y: cy };
    ["1", "2"].forEach(dStr => {
      const d = Number(dStr);
      const arr = byDepth[d] || [];
      const r = d === 1 ? RADIUS_INNER : RADIUS_OUTER;
      arr.forEach((n, i) => {
        const angle = (2 * Math.PI * i) / Math.max(arr.length, 1);
        positions[`${n.type}:${n.id}`] = { x: cx + r * Math.cos(angle), y: cy + r * Math.sin(angle) };
      });
    });
    return positions;
  }, [graph]);

  return (
    <div className="max-w-7xl mx-auto p-6" data-testid="graph-explorer">
      <Toaster position="top-right" richColors/>
      <Breadcrumbs items={[{ label: "Knowledge Graph" }, { label: "Graph Explorer" }]}/>
      <div className="font-serif text-3xl text-navy mt-4 mb-6 flex items-center gap-3"><Network size={22}/> Graph Explorer</div>

      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12 lg:col-span-4 space-y-3">
          <div className="bg-white border border-line p-4 space-y-2">
            <div className="mono text-[10px] uppercase tracking-widest text-prestige">Pick an entity</div>
            <select data-testid="gx-type" value={entityType} onChange={e => { setEntityType(e.target.value); setSelected(null); setGraph(null); }} className="w-full border border-line px-3 py-2 text-sm">
              {types.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
            <input data-testid="gx-search" value={q} onChange={e => setQ(e.target.value)} placeholder="Search by name / slug…" className="w-full border border-line px-3 py-2 text-sm"/>
            <div className="max-h-72 overflow-y-auto border border-line divide-y divide-line">
              {candidates.map(c => (
                <button key={c.id} data-testid={`gx-cand-${c.id}`} onClick={() => load(c.id)}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-clinical/60 ${selected === c.id ? "bg-clinical" : ""}`}>
                  <div className="text-navy truncate">{c.title || c.slug}</div>
                  <div className="mono text-[10px] text-stone2">{c.slug} · {c.status || "—"}</div>
                </button>
              ))}
              {candidates.length === 0 && <div className="text-xs text-stone2 px-3 py-3">No matches.</div>}
            </div>
          </div>
          <div className="bg-white border border-line p-4">
            <div className="mono text-[10px] uppercase tracking-widest text-prestige mb-2">Hops</div>
            <div className="flex gap-2">
              {[1, 2].map(h => (
                <button key={h} data-testid={`gx-hops-${h}`} onClick={() => setHops(h)}
                        className={`mono text-[10px] uppercase tracking-widest px-3 py-1.5 border ${hops === h ? "border-navy bg-navy text-white" : "border-line text-stone2 hover:border-navy"}`}>{h}-hop</button>
              ))}
            </div>
          </div>
        </div>

        <div className="col-span-12 lg:col-span-8">
          <div className="bg-white border border-line" style={{ minHeight: 520 }}>
            {!graph ? (
              <div className="p-10 text-center text-sm text-stone2">Select an entity to visualise its neighborhood.</div>
            ) : (
              <>
                <svg viewBox="0 0 760 520" className="w-full h-[520px]" data-testid="graph-svg">
                  {graph.edges.map(e => {
                    const s = layout[e.source], t = layout[e.target];
                    if (!s || !t) return null;
                    const stroke = e.is_directional ? (e.verified ? "#3F6250" : "#D97706") : "#9CA3AF";
                    return (
                      <g key={e.id} data-testid={`gx-edge-${e.id}`}>
                        <line x1={s.x} y1={s.y} x2={t.x} y2={t.y} stroke={stroke} strokeWidth={e.verified ? 1.6 : 1} strokeDasharray={e.is_directional && !e.verified ? "4 3" : "0"}/>
                      </g>
                    );
                  })}
                  {graph.nodes.map(n => {
                    const pos = layout[`${n.type}:${n.id}`];
                    if (!pos) return null;
                    const isRoot = `${n.type}:${n.id}` === graph.root;
                    return (
                      <g key={`${n.type}:${n.id}`} data-testid={`gx-node-${n.id}`}>
                        <circle cx={pos.x} cy={pos.y} r={isRoot ? 22 : 14}
                                fill={isRoot ? "#141B34" : "#fff"} stroke="#141B34" strokeWidth={1.5}/>
                        <text x={pos.x} y={pos.y + (isRoot ? 42 : 30)} textAnchor="middle"
                              fontFamily="sans-serif" fontSize={11} fill="#141B34">
                          {(n.title || n.slug || "").slice(0, 28)}
                        </text>
                        <text x={pos.x} y={pos.y + (isRoot ? 56 : 44)} textAnchor="middle"
                              fontFamily="ui-monospace" fontSize={9} fill="#6B7280">
                          {n.type.replace(/_/g, " ")}
                        </text>
                      </g>
                    );
                  })}
                </svg>
                <div className="border-t border-line px-4 py-3 flex items-center gap-4 mono text-[10px] uppercase tracking-widest text-stone2">
                  <span><span className="inline-block w-3 h-0.5 bg-[#9CA3AF] align-middle mr-1"></span>Neutral</span>
                  <span><span className="inline-block w-3 h-0.5 bg-[#3F6250] align-middle mr-1"></span>Directional · verified</span>
                  <span><span className="inline-block w-3 border-t border-dashed border-[#D97706] align-middle mr-1"></span>Directional · unverified</span>
                  <span className="ml-auto">{graph.nodes.length} nodes · {graph.edges.length} edges</span>
                </div>
              </>
            )}
          </div>
          {graph && (
            <ul className="mt-4 space-y-1 text-xs">
              {graph.nodes.filter(n => `${n.type}:${n.id}` !== graph.root).slice(0, 24).map(n => {
                const route = COLL_TO_TYPE_ROUTE[n.type];
                return (
                  <li key={`${n.type}:${n.id}`} className="flex items-center gap-2">
                    <span className="mono text-[10px] uppercase tracking-widest text-stone2 shrink-0">{n.type.replace(/_/g, " ")}</span>
                    {route ? (
                      <Link to={`/admin/content/${route}/${n.id}`} className="text-navy hover:text-terracotta inline-flex items-center gap-1 truncate">
                        {n.title || n.slug} <ExternalLink size={10}/>
                      </Link>
                    ) : <span className="text-navy truncate">{n.title || n.slug}</span>}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
